import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50" }
const _hoisted_2 = { class: "hidden xl:flex items-center gap-4" }
const _hoisted_3 = { class: "w-full px-6 py-8" }
const _hoisted_4 = { class: "mb-8" }
const _hoisted_5 = { class: "relative rounded-lg border border-gray-200 bg-white p-6 shadow-sm" }
const _hoisted_6 = { class: "mt-8 border-t border-gray-200 pt-2 lg:px-6" }
const _hoisted_7 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-8" }
const _hoisted_8 = { class: "mt-6 border-t border-gray-200 pt-4 lg:px-6" }

import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { 
  PageHeader, 
  ActionButton,
  DashboardStatsSection,
  CartProductsSection,
  ModalComp,
  ProductSalesStatsSection,
  BuyerSalesStatsSection,
  CountryClientsChart,
  PeriodSelector,
  RevenueChart,
  AnnouncementBanner
} from '@/components'
import { DashboardStats, ProduitRow, AcheteurRow, PeriodOption, TopAcheteurApi, TopReferenceApi, RepartitionClientsPaysApi, ChiffreAffaireSeries, PanierAcheteurRow } from '@/types'
import { CommandesPrestationsApi } from '@/api/commandesPrestations'
import { getTotalProduitsByVendeur } from '@/api/produits'
import { useAuthStore } from '@/stores/auth'
import { useOrdersStore } from '@/stores/ordersStore'
import { getActiveAnnouncements, type NewsItem } from '@/api/announcements'
// Router
const PRODUITS_LIMIT = 10
const ACHETEURS_LIMIT = 10

// Cache localStorage helpers
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes par défaut

export default /*@__PURE__*/_defineComponent({
  __name: 'HomeView',
  setup(__props) {

const router = useRouter()
const authStore = useAuthStore()
const ordersStore = useOrdersStore()

// État global
// Période sélectionnée depuis l'entête (synchronisée avec le graphique)
const selectedPeriod = ref<'jour' | 'semaine' | 'mois' | 'annee'>('jour')
const selectedChartPeriod = ref<'jour' | 'semaine' | 'mois' | 'annee'>('jour')
const selectedStart = ref<string | null>(null) // YYYY-MM-DD
const selectedEnd = ref<string | null>(null)   // YYYY-MM-DD
const currentVendorId = ref<number | null>(null)
const isLoadingProduits = ref(false)
const isLoadingAcheteurs = ref(false)
const isLoadingRepartitionPays = ref(false)
const isLoadingChart = ref(true) // Initialiser à true pour afficher le spinner au chargement initial
const isLoadingStats = ref(true) // Initialiser à true pour afficher le spinner au chargement initial

// Limites fixes pour les tableaux (toujours 10)
function safeGetCache<T = unknown>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const expires = parsed.expires as number | undefined
    if (!expires || Date.now() > expires) {
      // expiré
      try { localStorage.removeItem(key) } catch { /* ignore storage remove errors */ void 0 }
      return null
    }
    return parsed.value as T
  } catch {
    return null
  }
}

const safeSetCache = (key: string, value: unknown, ttlMs: number = CACHE_TTL_MS) => {
  try {
    const payload = { expires: Date.now() + Math.max(1000, ttlMs), value }
    localStorage.setItem(key, JSON.stringify(payload))
  } catch {
    // ignore stockage erreurs (quota, privacy, etc.)
    void 0
  }
}

// Options de période (alignées avec l'API et le graphique)
const periodOptions: PeriodOption[] = [
  { value: 'jour', label: 'Aujourd\'hui' },
  { value: 'semaine', label: 'Cette semaine' },
  { value: 'mois', label: 'Ce mois' },
  { value: 'annee', label: 'Cette année' }
]

const productStatsPeriodLabel = computed(() => {
  const { start, end } = getActiveDashboardRange()
  return `(${formatDisplayDate(start)} - ${formatDisplayDate(end)})`
})

const activeDashboardRangeParam = computed(() => getActiveDashboardRangeParam())

// Données du dashboard
const stats = ref<DashboardStats>({
  chiffreAffaires: 0,
  totalCommandes: 0,
  commandesEnAttente: 0,
  chiffreAffairesChange: 0,
  commandesChange: 0,
  totalProduits: 0,
  enAttenteChange: null
})

const produitsRows = ref<ProduitRow[]>([])
const isLoadingPaniers = ref(true)
const isPanierModalVisible = ref(false)
const panierRows = ref<PanierAcheteurRow[]>([])
const panierSummary = ref({ acheteurs: 0, montant: 0, commandeMini: 0 })
const acheteursRows = ref<AcheteurRow[]>([])
const repartitionPaysRows = ref<RepartitionClientsPaysApi[]>([])
const announcements = ref<NewsItem[]>([])

// Données du graphique de revenus
const revenueChartData = ref({
  currentPeriod: [] as number[],
  lastYear: [] as number[],
  labels: [] as string[]
})

const computePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current === 0 ? 0 : 100
  }

  return Math.round(((current - previous) / previous) * 100)
}

const formatCurrency = (value: number) =>
  value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const parseJourValue = (value: string | number): Date | null => {
  if (typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (!value) {
    return null
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return null
  }
  if (/^\d+$/.test(trimmed)) {
    const ts = trimmed.length === 13 ? Number(trimmed) : Number(trimmed) * 1000
    const d = new Date(ts)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(`${trimmed}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

// Helpers for period aggregation/labeling
const getISOWeek = (date: Date) => {
  // Copy date and set to nearest Thursday: current date + 4 - current day number
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7 // Monday=1..Sunday=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  return { week: weekNo, year: d.getUTCFullYear() }
}

// ISO week key helper (YYYY-Www)
const isoWeekKey = (date: Date): string => {
  const ws = startOfISOWeek(date)
  const { week, year } = getISOWeek(ws)
  return `${year}-W${String(week).padStart(2, '0')}`
}

// Build label for weeks; keep data aggregation key as ISO week.
// Requested format: use the month of the week and display as `YYYY-MM-Sn`,
// where `n` is the week number within that month (1-based).
// Attribution rule: the week belongs to the month of its Sunday (week end).
const getWeekKeyLabel = (date: Date): { key: string; label: string } => {
  const weekStart = startOfISOWeek(date)
  const key = isoWeekKey(weekStart) // aggregation key stays ISO

  // Determine the month attribution using Sunday (UTC)
  const weekEnd = new Date(weekStart)
  weekEnd.setUTCDate(weekEnd.getUTCDate() + 6) // Sunday (UTC)

  const year = weekEnd.getUTCFullYear()
  const month = weekEnd.getUTCMonth() // 0-11
  const monthStr = String(month + 1).padStart(2, '0')

  // First day of that month
  const firstOfMonth = new Date(Date.UTC(year, month, 1))
  // Start (Monday) of the first ISO week that intersects this month
  const firstWeekStartOfMonth = startOfISOWeek(firstOfMonth)

  // Compute week index within the month (1-based)
  const diffWeeks = Math.floor((weekStart.getTime() - firstWeekStartOfMonth.getTime()) / (7 * 86400000))
  const n = diffWeeks + 1

  // Display label uses 'S' (semaine) instead of 'W', format jj/mm/aa-Sn
  const yearShort = String(year).slice(-2)
  // Utiliser le premier jour de la semaine (lundi) pour le format
  const day = String(weekStart.getUTCDate()).padStart(2, '0')
  const label = `${day}/${monthStr}/${yearShort}-S${n}`
  return { key, label }
}

const startOfISOWeek = (date: Date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const day = d.getUTCDay() || 7
  if (day > 1) {
    d.setUTCDate(d.getUTCDate() - (day - 1))
  }
  return d
}

const formatISODate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDisplayDate = (isoDate: string) => {
  const [year, month, day] = isoDate.split('-')
  return `${day}/${month}/${year}`
}

const getRangeConfig = (period: string, override?: { start?: Date | null; end?: Date | null }) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let start: Date
  let end: Date
  let granularity: 'day' | 'week' | 'month' | 'year' = 'day'

  switch (period) {
    case 'semaine': {
      // Show weekly curve: X axis as weeks (ISO), aggregate 7 days per point.
      // Build last 13 ISO weeks window ending at next ISO Monday (exclusive).
      granularity = 'week'
      if (override?.start && override?.end) {
        start = new Date(override.start)
        end = new Date(override.end)
        // move end to next day to make exclusive in loops
        end.setDate(end.getDate() + 1)
      } else {
        const currWeekStart = startOfISOWeek(new Date(today))
        const nextWeekStart = new Date(currWeekStart)
        nextWeekStart.setUTCDate(nextWeekStart.getUTCDate() + 7)
        end = nextWeekStart
        start = new Date(nextWeekStart)
        start.setUTCDate(start.getUTCDate() - 7 * 13)
      }
      break
    }
    case 'mois': {
      // Monthly view: show one point per month over the last 4 months (current month included)
      // Align to month boundaries to match screenshot labels like YYYY-MM
      granularity = 'month'
      if (override?.start && override?.end) {
        const s = new Date(override.start)
        const e = new Date(override.end)
        start = new Date(s.getFullYear(), s.getMonth(), 1)
        end = new Date(e.getFullYear(), e.getMonth() + 1, 1)
      } else {
        // Start at the first day of the month, 3 months ago
        start = new Date(today.getFullYear(), today.getMonth() - 3, 1)
        // End at the first day of next month (exclusive)
        end = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      }
      break
    }
    case 'annee': {
      granularity = 'month'
      if (override?.start && override?.end) {
        const s = new Date(override.start)
        const e = new Date(override.end)
        start = new Date(s.getFullYear(), s.getMonth(), 1)
        end = new Date(e.getFullYear(), e.getMonth() + 1, 1)
      } else {
        start = new Date(today.getFullYear(), today.getMonth() - 11, 1)
        end = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      }
      break
    }
    case 'jour':
    default: {
      // Granularité quotidienne sur 14 jours
      granularity = 'day'
      if (override?.start && override?.end) {
        start = new Date(override.start)
        end = new Date(override.end)
        end.setDate(end.getDate() + 1)
      } else {
        // 14 jours : aujourd'hui exclus, remonter 13 jours en arrière
        end = new Date(today)
        end.setDate(end.getDate())
        start = new Date(today)
        start.setDate(start.getDate() - 14)
      }
      break
    }
  }

  return { start, end, granularity }
}

const getActiveDashboardRange = () => {
  if (selectedStart.value && selectedEnd.value) {
    return {
      start: selectedStart.value,
      end: selectedEnd.value,
    }
  }

  const { start, end } = getRangeConfig(selectedChartPeriod.value)
  const inclusiveEnd = new Date(end)
  inclusiveEnd.setDate(inclusiveEnd.getDate() - 1)

  return {
    start: formatISODate(start),
    end: formatISODate(inclusiveEnd),
  }
}

const getActiveDashboardRangeParam = () => {
  const { start, end } = getActiveDashboardRange()
  return `${start}..${end}`
}

const mapTopAcheteursToRows = (items: TopAcheteurApi[]): AcheteurRow[] => {
  return items.map(item => ({
    id: item.id_acheteur,
    nom: item.nom ?? `Acheteur #${item.id_acheteur}`,
    commandes: item.commandes ?? 0,
    montant: formatCurrency(item.montant_total ?? 0),
    derniereCommande: '—'
  }))
}

const mapTopReferencesToRows = (items: TopReferenceApi[]): ProduitRow[] => {
  const currentYear = new Date().getFullYear()
  return items.map(item => ({
    id: item.id_produit,
    reference: item.reference ?? `REF-${item.id_produit}`,
    imageUrl: item.imageUrl ?? null,
    categorie: item.categorie ?? null,
    ventes: item.quantite ?? 0,
    chiffreAffaires: formatCurrency(item.chiffre_affaires ?? 0),
    evolution: `${currentYear}`,
  }))
}

const loadRevenueChartData = async (idVendeur: number, period: string, start?: string | null, end?: string | null) => {
  try {
    isLoadingChart.value = true
    // Ne plus utiliser localStorage pour mettre en cache les données du graphique
    // Build range string for API if explicit dates are provided
    const rangeParam = start && end ? `${start}..${end}` : period
    const rawData = await CommandesPrestationsApi.getChiffreAffaireParJour(idVendeur, rangeParam, true)
    const { labels, currentValues, previousValues } = buildChartDataFromDaily(rawData, period, { start, end })
    const built = {
      labels,
      currentPeriod: currentValues,
      lastYear: previousValues,
    }
    // Mettre à jour les données d'abord
    revenueChartData.value = built
    // Attendre que Vue ait mis à jour le DOM
    await nextTick()
  } catch (error) {
    revenueChartData.value = { labels: [], currentPeriod: [], lastYear: [] }
  } finally {
    // Mettre isLoading à false seulement après que les données soient mises à jour
    isLoadingChart.value = false
  }
}

// Méthodes
const loadProduitsEnPanier = async () => {
  isLoadingPaniers.value = true
  try {
    const idVendeur = authStore.user?.id
    if (!idVendeur) {
      panierRows.value = []
      panierSummary.value = { acheteurs: 0, montant: 0, commandeMini: 0 }
      return
    }

    const data = await CommandesPrestationsApi.getProduitsEnPanier(idVendeur, 200)
    panierSummary.value = {
      acheteurs: data.acheteurs ?? 0,
      montant: data.montant ?? 0,
      commandeMini: data.commandeMini ?? 0,
    }
    panierRows.value = (data.paniers ?? []).map((panier) => ({
      id: panier.id_acheteur,
      nom: panier.nom,
      montant: panier.montant ?? 0,
      date: panier.date ?? null,
      pays: panier.pays ?? null,
      produits: (panier.produits ?? []).map((produit) => ({
        id: produit.id_produit,
        reference: produit.reference || `Produit #${produit.id_produit}`,
        imageUrl: produit.imageUrl,
        categorie: produit.categorie,
        quantite: produit.quantite ?? 0,
        rupture: Boolean(produit.rupture),
      })),
    }))
  } catch {
    panierRows.value = []
    panierSummary.value = { acheteurs: 0, montant: 0, commandeMini: 0 }
  } finally {
    isLoadingPaniers.value = false
  }
}

const loadDashboardData = async () => {
  isLoadingProduits.value = true
  isLoadingAcheteurs.value = true
  isLoadingRepartitionPays.value = true

  try {
    produitsRows.value = []
    acheteursRows.value = []
    repartitionPaysRows.value = []

    const idVendeur = authStore.user?.id
    if (!idVendeur) {
      throw new Error('Aucun vendeur connecté : impossible de charger les statistiques.')
    }

    // Tenter de charger depuis le cache local
    const rangeParam = getActiveDashboardRangeParam()
    const dashKey = `dashboard:data:v2:${idVendeur}:${selectedChartPeriod.value}:${rangeParam}`
    const totalKey = `dashboard:totalProduits:${idVendeur}`
    let dashboard = safeGetCache<any>(dashKey)
    let totalProduits: number | string | null = safeGetCache<number | string>(totalKey)

    if (!dashboard || totalProduits == null) {
      const [fetchedDash, fetchedTotal] = await Promise.all([
        CommandesPrestationsApi.getDashboard(
            idVendeur,
            rangeParam,
            ACHETEURS_LIMIT,            // top acheteurs (toujours 10)
            PRODUITS_LIMIT,             // top références (toujours 10)
            selectedChartPeriod.value
        ),
        getTotalProduitsByVendeur(idVendeur),
      ])
      dashboard = fetchedDash
      totalProduits = fetchedTotal as any
      // Stocker au cache
      safeSetCache(dashKey, dashboard)
      // total produits varie peu, TTL un peu plus long (15 min)
      safeSetCache(totalKey, totalProduits, 15 * 60 * 1000)
    }

    const caCourant = Number((dashboard.caCourant ?? 0).toFixed(2))
    const caPrecedent = Number((dashboard.caPrecedent ?? 0).toFixed(2))
    const commandesCourantes = dashboard.commandesCourantes ?? 0
    const commandesPrecedentes = dashboard.commandesPrecedentes ?? 0

    stats.value.chiffreAffaires = caCourant
    stats.value.totalCommandes = commandesCourantes
    stats.value.commandesEnAttente = dashboard.enAttente ?? 0

    const produitsCount = typeof totalProduits === 'number'
        ? totalProduits
        : parseInt(String(totalProduits ?? 0), 10)

    stats.value.totalProduits = Number.isNaN(produitsCount) ? 0 : produitsCount
    stats.value.chiffreAffairesChange = computePercentageChange(caCourant, caPrecedent)
    stats.value.commandesChange = computePercentageChange(commandesCourantes, commandesPrecedentes)
    stats.value.enAttenteChange = null

    currentVendorId.value = idVendeur

    await loadRevenueChartData(
      idVendeur,
      selectedChartPeriod.value,
      selectedStart.value,
      selectedEnd.value,
    )

    produitsRows.value = mapTopReferencesToRows(dashboard.topReferences ?? [])
    acheteursRows.value = mapTopAcheteursToRows(dashboard.topAcheteurs ?? [])
    repartitionPaysRows.value = dashboard.repartitionPays ?? []
  } catch (error) {
    // Erreur silencieuse
  } finally {
    isLoadingProduits.value = false
    isLoadingAcheteurs.value = false
    isLoadingRepartitionPays.value = false
    isLoadingStats.value = false
  }
}

const buildSeriesMap = (
  points: Array<{ jour: string | number; total: number }>,
  granularity: 'day' | 'week' | 'month' | 'year',
  yearShift = 0,
) => {
  const map = new Map<string, number>()

  points.forEach(item => {
    const parsed = parseJourValue(item.jour)
    if (!parsed) {
      return
    }
    const adjusted = new Date(parsed)
    if (yearShift !== 0) {
      adjusted.setFullYear(adjusted.getFullYear() + yearShift)
    }
    let key: string
    if (granularity === 'year') {
      key = `${adjusted.getFullYear()}`
    } else if (granularity === 'month') {
      key = `${adjusted.getFullYear()}-${String(adjusted.getMonth() + 1).padStart(2, '0')}`
    } else if (granularity === 'week') {
      key = isoWeekKey(adjusted)
    } else {
      key = `${adjusted.getFullYear()}-${String(adjusted.getMonth() + 1).padStart(2, '0')}-${String(adjusted.getDate()).padStart(2, '0')}`
    }
    map.set(key, (map.get(key) ?? 0) + (item.total ?? 0))
  })

  return map
}

const buildChartDataFromDaily = (
  series: ChiffreAffaireSeries,
  period: string,
  override?: { start?: string | null; end?: string | null },
) => {
  const parseISO = (s?: string | null) => (s ? new Date(s + 'T00:00:00') : null)
  const { start, end, granularity } = getRangeConfig(period, {
    start: parseISO(override?.start ?? null),
    end: parseISO(override?.end ?? null),
  })
  const currentMap = buildSeriesMap(series.current ?? [], granularity)
  const previousMap = buildSeriesMap(series.previous ?? [], granularity, 1)
  const labels: string[] = []
  const currentValues: number[] = []
  const previousValues: number[] = []

  const formatYYYYMMDD = (d: Date) => {
    const y = String(d.getFullYear()).slice(-2) // Prendre les 2 derniers chiffres
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${day}/${m}/${y}` // Format jj/mm/aa
  }

  if (granularity === 'year') {
    const cursor = new Date(start)
    while (cursor < end) {
      const key = `${cursor.getFullYear()}`
      labels.push(String(cursor.getFullYear()).slice(-2)) // Prendre les 2 derniers chiffres
      currentValues.push(Number((currentMap.get(key) ?? 0).toFixed(2)))
      previousValues.push(Number((previousMap.get(key) ?? 0).toFixed(2)))
      cursor.setFullYear(cursor.getFullYear() + 1)
    }
  } else if (granularity === 'month') {
    const cursor = new Date(start)
    while (cursor < end) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`
      // Format label MM/YY pour les mois
      const yearShort = String(cursor.getFullYear()).slice(-2)
      const month = String(cursor.getMonth() + 1).padStart(2, '0')
      labels.push(`${month}/${yearShort}`) // Format MM/YY
      currentValues.push(Number((currentMap.get(key) ?? 0).toFixed(2)))
      previousValues.push(Number((previousMap.get(key) ?? 0).toFixed(2)))
      cursor.setMonth(cursor.getMonth() + 1)
    }
  } else if (granularity === 'week') {
    let cursor = startOfISOWeek(new Date(start))
    while (cursor < end) {
      const { key, label } = getWeekKeyLabel(cursor)
      // Labels for weekly view: ISO Year-Week normally; special case for November: YYYY-11-Wn
      labels.push(label)
      currentValues.push(Number((currentMap.get(key) ?? 0).toFixed(2)))
      previousValues.push(Number((previousMap.get(key) ?? 0).toFixed(2)))
      cursor = new Date(cursor)
      cursor.setUTCDate(cursor.getUTCDate() + 7)
    }
  } else {
    const cursor = new Date(start)
    while (cursor < end) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`
      // Use ISO-like label so the chart can reliably determine Mondays on X-axis
      labels.push(formatYYYYMMDD(cursor))
      currentValues.push(Number((currentMap.get(key) ?? 0).toFixed(2)))
      previousValues.push(Number((previousMap.get(key) ?? 0).toFixed(2)))
      cursor.setDate(cursor.getDate() + 1)
    }
  }

  const hasPrevious = (series.previous?.length ?? 0) > 0

  return { labels, currentValues, previousValues: hasPrevious ? previousValues : [] }
}

const openStatsModal = (type: string) => {
  switch (type) {
    case 'commandes':
      ordersStore.clearFilters()
      router.push({ name: 'myorders' })
      break

    case 'enAttente':
      ordersStore.setFilters({ status: '1', orderId: '', carrier: '', orderDate: '', shippingDate: '', buyer: '', origin: '' })
      router.push({ name: 'myorders' })
      break
  }
}

const exportData = () => {
  // Export des données du dashboard
}

// Gestion du changement de période pour le graphique
const handlePeriodChange = (period: string) => {
  const normalized = (['jour', 'semaine', 'mois'].includes(period)
    ? period
    : 'jour') as typeof selectedChartPeriod.value
  selectedChartPeriod.value = normalized
  selectedPeriod.value = normalized
  // Réinitialiser les dates selon la nouvelle période (null = utiliser les dates par défaut de la période)
  selectedStart.value = null
  selectedEnd.value = null
  loadDashboardData()
}

// Reception du changement de date range depuis le graphique
const handleDateRangeChange = (start: string, end: string) => {
  // Ne mettre à jour que si les dates sont différentes pour éviter les appels multiples
  if (selectedStart.value !== start || selectedEnd.value !== end) {
    selectedStart.value = start
    selectedEnd.value = end
    loadDashboardData()
  }
}

// Plus de gestionnaires de changement de limite - affichage fixe de 10 éléments

// Synchroniser le sélecteur d'entête avec le graphique et les indicateurs
watch(selectedPeriod, (newVal) => {
  const normalized = (['jour', 'semaine', 'mois', 'annee'].includes(newVal)
    ? newVal
    : 'jour') as typeof selectedChartPeriod.value
  if (selectedChartPeriod.value === normalized) {
    return
  }
  // Mettre à jour la période du graphique si nécessaire
  selectedChartPeriod.value = normalized
  // Si la période change depuis le sélecteur d'en-tête, réinitialiser les dates
  selectedStart.value = null
  selectedEnd.value = null
  // Invalider puis recharger le tableau de bord pour recalculer les KPI
  loadDashboardData()
})

onMounted(async () => {
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  if (!authStore.isAuthenticated && authStore.isImpersonating) {
    await authStore.checkAuth({ force: true });
  }

  if (!authStore.isAuthenticated) {
    if (authStore.isImpersonating) {
      router.replace({ path: "/adminEFP" });
    } else {
      router.push({ name: "auth" });
    }
    return;
  }
  
  const idVendeur = authStore.user?.id
  if (idVendeur) {
    currentVendorId.value = idVendeur
  }
  
  // initialiser selectedPeriod sur la valeur du graphique
  selectedPeriod.value = selectedChartPeriod.value
  loadDashboardData()
  loadProduitsEnPanier()

  // Charger les annonces
  try {
    announcements.value = await getActiveAnnouncements()
  } catch (e) {
    console.warn('Impossible de charger les annonces', e)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(PageHeader), {
      title: "Tableau de bord",
      subtitle: "Statistiques et aperçu de vos activités"
    }, {
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_unref(PeriodSelector), {
            modelValue: selectedPeriod.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedPeriod).value = $event)),
            "period-options": periodOptions
          }, null, 8, ["modelValue"]),
          _createVNode(_unref(ActionButton), {
            label: "Exporter",
            icon: "fas fa-download",
            variant: "secondary",
            onClick: exportData
          })
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(DashboardStatsSection), {
        stats: stats.value,
        "is-loading": isLoadingStats.value,
        "is-loading-paniers": isLoadingPaniers.value,
        "acheteurs-en-panier": panierSummary.value.acheteurs,
        "open-stats-modal": openStatsModal,
        onOpenPaniers: _cache[1] || (_cache[1] = ($event: any) => (isPanierModalVisible.value = true))
      }, null, 8, ["stats", "is-loading", "is-loading-paniers", "acheteurs-en-panier"]),
      _createVNode(_unref(ModalComp), {
        title: "Paniers en cours",
        "is-visible": isPanierModalVisible.value,
        size: "xl",
        "hide-footer": "",
        "onUpdate:isVisible": _cache[2] || (_cache[2] = ($event: any) => (isPanierModalVisible.value = $event))
      }, {
        body: _withCtx(() => [
          _createVNode(_unref(CartProductsSection), {
            paniers: panierRows.value,
            loading: isLoadingPaniers.value,
            acheteurs: panierSummary.value.acheteurs,
            montant: panierSummary.value.montant,
            "commande-mini": panierSummary.value.commandeMini
          }, null, 8, ["paniers", "loading", "acheteurs", "montant", "commande-mini"])
        ]),
        _: 1
      }, 8, ["is-visible"]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(_unref(AnnouncementBanner), { announcements: announcements.value }, null, 8, ["announcements"])
      ]),
      _createElementVNode("section", _hoisted_5, [
        _cache[3] || (_cache[3] = _createElementVNode("div", null, [
          _createElementVNode("h2", { class: "text-xl font-semibold text-black" }, "Aperçu")
        ], -1)),
        _createVNode(_unref(RevenueChart), {
          class: "mt-6 dashboard-revenue-chart",
          frameless: "",
          "current-period-data": revenueChartData.value.currentPeriod,
          "last-year-data": revenueChartData.value.lastYear,
          labels: revenueChartData.value.labels,
          "is-loading": isLoadingChart.value,
          onPeriodChange: handlePeriodChange,
          onDateRangeChange: handleDateRangeChange
        }, null, 8, ["current-period-data", "last-year-data", "labels", "is-loading"]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_unref(ProductSalesStatsSection), {
              frameless: "",
              rows: produitsRows.value.slice(0, 10),
              loading: isLoadingProduits.value,
              "period-label": productStatsPeriodLabel.value
            }, null, 8, ["rows", "loading", "period-label"]),
            _createVNode(_unref(BuyerSalesStatsSection), {
              frameless: "",
              rows: acheteursRows.value.slice(0, 10),
              loading: isLoadingAcheteurs.value,
              "period-label": productStatsPeriodLabel.value
            }, null, 8, ["rows", "loading", "period-label"])
          ])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(CountryClientsChart), {
            frameless: "",
            rows: repartitionPaysRows.value,
            loading: isLoadingRepartitionPays.value,
            "period-label": productStatsPeriodLabel.value,
            "seller-id": currentVendorId.value,
            range: activeDashboardRangeParam.value
          }, null, 8, ["rows", "loading", "period-label", "seller-id", "range"])
        ])
      ])
    ])
  ]))
}
}

})